import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getArticle, updateArticle, getCategories, getDestinations, getPhotos, getArticleAiAssistance, updateArticleAiAssistance } from "../lib/api";
import { getThumbnailUrl } from "../lib/cloudinaryUtils";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, Save, Eye, Loader2, Trash2, GripVertical, 
  Image as ImageIcon, Type, Quote, AlignLeft, Plus, Check, X,
  ChevronUp, ChevronDown, FileText, RotateCcw, RotateCw, RefreshCw
} from "lucide-react";
import TranslationsPanel from "../components/admin/TranslationsPanel";

// Block type icons and labels
const BLOCK_TYPES = {
  section_title: { icon: Type, label: "Titre de section", color: "bg-blue-100 text-blue-700" },
  paragraph: { icon: AlignLeft, label: "Paragraphe", color: "bg-gray-100 text-gray-700" },
  quote: { icon: Quote, label: "Citation", color: "bg-purple-100 text-purple-700" },
  image: { icon: ImageIcon, label: "Image", color: "bg-green-100 text-green-700" },
  image_gallery: { icon: ImageIcon, label: "Galerie", color: "bg-amber-100 text-amber-700" }
};

// Single block editor component
function BlockEditor({ block, index, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, photos }) {
  const [expanded, setExpanded] = useState(true);
  const typeInfo = BLOCK_TYPES[block.type] || { icon: FileText, label: block.type, color: "bg-gray-100" };
  const IconComponent = typeInfo.icon;

  const handleTextChange = (e) => {
    onUpdate({ ...block, text: e.target.value });
  };

  const handleCaptionChange = (e) => {
    onUpdate({ ...block, caption: e.target.value });
  };

  // Helper to remove orientation transforms from URL
  const removeOrientationTransforms = (url) => {
    if (!url || !url.includes('/upload/')) return url;
    
    const parts = url.split('/upload/');
    if (parts.length !== 2) return url;
    
    const base = parts[0] + '/upload/';
    const rest = parts[1];
    const segments = rest.split('/');
    const cleanSegments = [];
    
    for (const seg of segments) {
      if (seg.includes('_') && !seg.startsWith('v') && !seg.includes('.')) {
        const transforms = seg.split(',');
        const cleanTransforms = transforms.filter(t => 
          !['a_90', 'a_180', 'a_270', 'e_hflip', 'e_vflip'].includes(t)
        );
        if (cleanTransforms.length > 0) {
          cleanSegments.push(cleanTransforms.join(','));
        }
      } else {
        cleanSegments.push(seg);
      }
    }
    
    return base + cleanSegments.join('/');
  };

  const getPhotoUrl = (photoId, blockRotation = 0) => {
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return null;
    
    // Get base cloudinary URL
    let url = photo.cloudinary_url;
    if (!url) {
      if (photo.image_data) {
        return `data:image/jpeg;base64,${photo.image_data}`;
      }
      return null;
    }
    
    // If block has rotation, remove existing orientation transforms and apply block's
    if (blockRotation && url.includes('/upload/')) {
      // Remove existing orientation transforms
      url = removeOrientationTransforms(url);
      
      // Apply thumbnail resize + block rotation
      const rotationTransform = blockRotation === 90 ? 'a_90' : 
                                blockRotation === 180 ? 'a_180' : 
                                blockRotation === 270 ? 'a_270' : '';
      const transforms = `c_fill,g_auto,w_100,h_100${rotationTransform ? ',' + rotationTransform : ''}`;
      url = url.replace('/upload/', `/upload/${transforms}/`);
    } else {
      // No block rotation - use standard thumbnail with photo's default transforms
      url = getThumbnailUrl(photo, 100);
    }
    
    return url;
  };

  const handleRotateBlock = (direction) => {
    const currentRotation = block.rotation || 0;
    let newRotation;
    
    if (direction === 'left') {
      newRotation = (currentRotation + 270) % 360; // -90° = +270°
    } else if (direction === 'right') {
      newRotation = (currentRotation + 90) % 360;
    } else {
      newRotation = 0; // reset
    }
    
    onUpdate({ ...block, rotation: newRotation });
  };

  const handleGalleryPhotoRotate = (photoId, direction) => {
    const rotations = block.rotations || {};
    const currentRotation = rotations[photoId] || 0;
    let newRotation;
    
    if (direction === 'left') {
      newRotation = (currentRotation + 270) % 360;
    } else if (direction === 'right') {
      newRotation = (currentRotation + 90) % 360;
    } else {
      newRotation = 0;
    }
    
    onUpdate({ 
      ...block, 
      rotations: { ...rotations, [photoId]: newRotation }
    });
  };

  const getGalleryPhotoRotation = (photoId) => {
    return (block.rotations || {})[photoId] || 0;
  };

  return (
    <div 
      className="bg-white border border-border rounded-lg overflow-hidden group"
      data-testid={`block-editor-${index}`}
    >
      {/* Block Header */}
      <div className="flex items-center gap-2 p-3 bg-muted/30 border-b">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMoveUp()}
            disabled={isFirst}
            className="p-1 hover:bg-muted rounded disabled:opacity-30"
            title="Monter"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            onClick={() => onMoveDown()}
            disabled={isLast}
            className="p-1 hover:bg-muted rounded disabled:opacity-30"
            title="Descendre"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeInfo.color}`}>
            <IconComponent className="h-3 w-3 inline mr-1" />
            {typeInfo.label}
          </span>
          <span className="text-xs text-muted-foreground">#{index + 1}</span>
          {block.type === "paragraph" && block.text && (
            <span className="text-xs text-muted-foreground truncate">
              {block.text.substring(0, 50)}...
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-muted rounded"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={onDelete}
            className="p-1 hover:bg-red-100 text-red-500 rounded"
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Block Content */}
      {expanded && (
        <div className="p-4 space-y-3">
          {/* Text-based blocks */}
          {["section_title", "paragraph", "quote"].includes(block.type) && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">
                {block.type === "section_title" ? "Titre" : block.type === "quote" ? "Citation" : "Texte"}
              </label>
              {block.type === "paragraph" ? (
                <Textarea
                  value={block.text || ""}
                  onChange={handleTextChange}
                  rows={4}
                  className="resize-none"
                  placeholder="Contenu du paragraphe..."
                />
              ) : (
                <Input
                  value={block.text || ""}
                  onChange={handleTextChange}
                  placeholder={block.type === "section_title" ? "Titre de section" : "Texte de la citation"}
                />
              )}
            </div>
          )}

          {/* Image block */}
          {block.type === "image" && (
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  <div className="w-20 h-20 bg-muted rounded overflow-hidden">
                    {block.photo_id && getPhotoUrl(block.photo_id, block.rotation) ? (
                      <img src={getPhotoUrl(block.photo_id, block.rotation)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
                      </div>
                    )}
                  </div>
                  {/* Rotation controls */}
                  {block.photo_id && (
                    <div className="flex items-center justify-center gap-1 mt-2">
                      <button
                        onClick={() => handleRotateBlock('left')}
                        className="p-1.5 bg-muted hover:bg-muted/80 rounded transition-colors"
                        title="Tourner à gauche (-90°)"
                        type="button"
                      >
                        <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleRotateBlock('right')}
                        className="p-1.5 bg-muted hover:bg-muted/80 rounded transition-colors"
                        title="Tourner à droite (+90°)"
                        type="button"
                      >
                        <RotateCw className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleRotateBlock('reset')}
                        className="p-1.5 bg-muted hover:bg-muted/80 rounded transition-colors"
                        title="Réinitialiser l'orientation"
                        type="button"
                        disabled={!block.rotation}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${block.rotation ? 'text-accent' : 'text-muted-foreground/30'}`} />
                      </button>
                    </div>
                  )}
                  {block.rotation ? (
                    <p className="text-xs text-center text-muted-foreground mt-1">{block.rotation}°</p>
                  ) : null}
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Photo ID</label>
                  <Input
                    value={block.photo_id || ""}
                    onChange={(e) => onUpdate({ ...block, photo_id: e.target.value })}
                    placeholder="ID de la photo"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Légende</label>
                <Input
                  value={block.caption || ""}
                  onChange={handleCaptionChange}
                  placeholder="Légende de l'image (optionnel)"
                />
              </div>
            </div>
          )}

          {/* Gallery block */}
          {block.type === "image_gallery" && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Photos ({block.photo_ids?.length || 0})
                </label>
                <div className="flex flex-wrap gap-3 mb-2">
                  {(block.photo_ids || []).map((pid, i) => {
                    const photoRotation = getGalleryPhotoRotation(pid);
                    return (
                      <div key={i} className="relative group/thumb">
                        <div className="w-16 h-16 bg-muted rounded overflow-hidden">
                          {getPhotoUrl(pid, photoRotation) ? (
                            <img src={getPhotoUrl(pid, photoRotation)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs font-mono">
                              {pid.substring(0, 6)}
                            </div>
                          )}
                        </div>
                        {/* Delete button */}
                        <button
                          onClick={() => {
                            const newIds = block.photo_ids.filter((_, idx) => idx !== i);
                            // Also remove rotation for this photo
                            const newRotations = { ...(block.rotations || {}) };
                            delete newRotations[pid];
                            onUpdate({ ...block, photo_ids: newIds, rotations: newRotations });
                          }}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover/thumb:opacity-100 z-10"
                          type="button"
                        >
                          <X className="h-3 w-3 mx-auto" />
                        </button>
                        {/* Rotation controls */}
                        <div className="flex items-center justify-center gap-0.5 mt-1">
                          <button
                            onClick={() => handleGalleryPhotoRotate(pid, 'left')}
                            className="p-1 bg-muted hover:bg-muted/80 rounded transition-colors"
                            title="Tourner à gauche (-90°)"
                            type="button"
                          >
                            <RotateCcw className="h-3 w-3 text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleGalleryPhotoRotate(pid, 'right')}
                            className="p-1 bg-muted hover:bg-muted/80 rounded transition-colors"
                            title="Tourner à droite (+90°)"
                            type="button"
                          >
                            <RotateCw className="h-3 w-3 text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleGalleryPhotoRotate(pid, 'reset')}
                            className="p-1 bg-muted hover:bg-muted/80 rounded transition-colors"
                            title="Réinitialiser"
                            type="button"
                            disabled={!photoRotation}
                          >
                            <RefreshCw className={`h-3 w-3 ${photoRotation ? 'text-accent' : 'text-muted-foreground/30'}`} />
                          </button>
                        </div>
                        {photoRotation ? (
                          <p className="text-[10px] text-center text-muted-foreground">{photoRotation}°</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <Input
                  placeholder="Ajouter photo ID (puis Entrée)"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      const newIds = [...(block.photo_ids || []), e.target.value.trim()];
                      onUpdate({ ...block, photo_ids: newIds });
                      e.target.value = "";
                    }
                  }}
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Légende commune</label>
                <Input
                  value={block.caption || ""}
                  onChange={handleCaptionChange}
                  placeholder="Légende de la galerie (optionnel)"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ArticleEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  // States
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [photos, setPhotos] = useState([]);
  
  // Form state
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [contentBlocks, setContentBlocks] = useState([]);
  const [coverPhotoId, setCoverPhotoId] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [tags, setTags] = useState("");
  const [status, setStatus] = useState("draft");
  // Phase 14 — métadonnées éditoriales / transparence / audit IA
  const [editorialType, setEditorialType] = useState("");
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [disclosureType, setDisclosureType] = useState("");
  const [disclosurePartner, setDisclosurePartner] = useState("");
  const [disclosureText, setDisclosureText] = useState("");
  const [correctionNote, setCorrectionNote] = useState("");
  const [correctionDate, setCorrectionDate] = useState("");
  const [aiMeta, setAiMeta] = useState(null);
  const [savingAi, setSavingAi] = useState(false);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [articleRes, catsRes, destsRes, photosRes] = await Promise.all([
          getArticle(id),
          getCategories(),
          getDestinations(),
          getPhotos({ limit: 500 })
        ]);
        
        const art = articleRes.data;
        setArticle(art);
        setTitle(art.title || "");
        setSubtitle(art.subtitle || "");
        setExcerpt(art.excerpt || "");
        setCategoryId(art.category_id || "");
        setDestinationId(art.destination_id || "");
        setContentBlocks(art.content_blocks || []);
        setCoverPhotoId(art.featured_image || "");
        setSeoTitle(art.seo_title || "");
        setMetaDescription(art.meta_description || "");
        setTags((art.tags || []).join(", "));
        setStatus(art.status || "draft");
        setEditorialType(art.editorial_type || "");
        setShowDisclosure(!!art.show_disclosure);
        setDisclosureType(art.disclosure_type || "");
        setDisclosurePartner(art.disclosure_partner || "");
        setDisclosureText(art.disclosure_text || "");
        setCorrectionNote(art.correction_note || "");
        setCorrectionDate(art.correction_date ? String(art.correction_date).slice(0, 10) : "");
        
        setCategories(catsRes.data);
        setDestinations(destsRes.data);
        setPhotos(photosRes.data);
        try {
          const aiRes = await getArticleAiAssistance(id);
          setAiMeta(aiRes.data || {});
        } catch {
          setAiMeta({});
        }
      } catch (err) {
        console.error("Error loading article:", err);
        toast.error("Erreur lors du chargement de l'article");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  // Calculate word count
  const wordCount = useCallback(() => {
    let count = 0;
    for (const block of contentBlocks) {
      if (["paragraph", "section_title", "quote"].includes(block.type) && block.text) {
        count += block.text.split(/\s+/).filter(Boolean).length;
      }
    }
    return count;
  }, [contentBlocks]);

  // Block operations
  const updateBlock = (index, newBlock) => {
    const newBlocks = [...contentBlocks];
    newBlocks[index] = newBlock;
    setContentBlocks(newBlocks);
  };

  const deleteBlock = (index) => {
    if (window.confirm("Supprimer ce bloc ?")) {
      setContentBlocks(contentBlocks.filter((_, i) => i !== index));
    }
  };

  const moveBlock = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= contentBlocks.length) return;
    
    const newBlocks = [...contentBlocks];
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    setContentBlocks(newBlocks);
  };

  // Save
  const handleSave = async (newStatus = null) => {
    if (!title.trim()) {
      toast.error("Le titre est requis");
      return;
    }
    if (!categoryId) {
      toast.error("La catégorie est requise");
      return;
    }
    
    setSaving(true);
    
    try {
      const finalStatus = newStatus || status;
      
      // Collect all photo IDs
      const allPhotoIds = new Set();
      if (coverPhotoId) allPhotoIds.add(coverPhotoId);
      for (const block of contentBlocks) {
        if (block.photo_id) allPhotoIds.add(block.photo_id);
        if (block.photo_ids) block.photo_ids.forEach(pid => allPhotoIds.add(pid));
      }
      
      const updateData = {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        excerpt: excerpt.trim() || null,
        category_id: categoryId,
        destination_id: destinationId || null,
        content_blocks: contentBlocks,
        featured_image: coverPhotoId || null,
        photo_ids: Array.from(allPhotoIds),
        seo_title: seoTitle.trim() || null,
        meta_description: metaDescription.trim() || null,
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        status: finalStatus,
        editorial_type: editorialType || null,
        show_disclosure: showDisclosure,
        disclosure_type: disclosureType.trim() || null,
        disclosure_partner: disclosurePartner.trim() || null,
        disclosure_text: disclosureText.trim() || null,
        correction_note: correctionNote.trim() || null,
        correction_date: correctionDate || null
      };
      
      await updateArticle(id, updateData);
      setStatus(finalStatus);
      
      const statusLabels = {
        draft: "brouillon",
        to_review: "à relire",
        validated: "validé",
        published: "publié",
        archived: "archivé"
      };
      
      toast.success(`Article enregistré (${statusLabels[finalStatus] || finalStatus})`);
    } catch (err) {
      console.error("Error saving:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Get photo thumbnail
  const getPhotoThumb = (photoId) => {
    const photo = photos.find(p => p.id === photoId);
    return getThumbnailUrl(photo, 80);
  };

  // Phase 14 STEP 4.4 — enregistrement manuel de l'audit assistance IA (interne)
  const handleSaveAi = async () => {
    setSavingAi(true);
    try {
      const res = await updateArticleAiAssistance(id, {
        ai_assistance_used: !!aiMeta?.ai_assistance_used,
        ai_assistance_types: aiMeta?.ai_assistance_types || [],
        human_reviewed: !!aiMeta?.human_reviewed,
      });
      setAiMeta(res.data);
      toast.success("Audit assistance IA enregistré");
    } catch {
      toast.error("Erreur lors de l'enregistrement de l'audit IA");
    } finally {
      setSavingAi(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-secondary pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-primary mb-4">Article non trouvé</h1>
          <Link to="/dashboard/articles">
            <Button>Retour aux articles</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-secondary pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-primary mb-4">Accès non autorisé</h1>
          <Link to="/">
            <Button>Retour à l&apos;accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="article-edit-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-20 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/dashboard/articles" className="text-muted-foreground hover:text-primary">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold text-primary line-clamp-1">
                  {title || "Sans titre"}
                </h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    status === "published" ? "bg-green-100 text-green-700" :
                    status === "draft" ? "bg-gray-100 text-gray-700" :
                    "bg-yellow-100 text-yellow-700"
                  }`}>
                    {status === "published" ? "Publié" : 
                     status === "draft" ? "Brouillon" :
                     status === "validated" ? "Validé" :
                     status === "to_review" ? "À relire" : status}
                  </span>
                  <span>{wordCount()} mots</span>
                  <span>{contentBlocks.length} blocs</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => window.open(`/article/${id}`, "_blank")}
                className="gap-2"
              >
                <Eye className="h-4 w-4" />
                Aperçu
              </Button>
              
              {status !== "published" && (
                <Button
                  variant="outline"
                  onClick={() => handleSave("draft")}
                  disabled={saving}
                  className="gap-2"
                  data-testid="save-draft-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Enregistrer
                </Button>
              )}
              
              {status === "draft" && (
                <Button
                  onClick={() => handleSave("published")}
                  disabled={saving}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="publish-btn"
                >
                  <Check className="h-4 w-4" />
                  Publier
                </Button>
              )}
              
              {status === "published" && (
                <Button
                  onClick={() => handleSave("draft")}
                  disabled={saving}
                  variant="outline"
                  className="gap-2"
                  data-testid="unpublish-btn"
                >
                  Repasser en brouillon
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content - blocks */}
          <div className="lg:col-span-2 space-y-4">
            {/* Title & Meta */}
            <div className="bg-white border rounded-lg p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Titre *</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Titre de l'article"
                  className="text-lg font-semibold"
                  data-testid="title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Sous-titre</label>
                <Input
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Sous-titre (optionnel)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Chapô / Extrait</label>
                <Textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Résumé accrocheur de l'article..."
                  rows={2}
                />
              </div>
            </div>

            {/* Content Blocks */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-primary">
                  Contenu ({contentBlocks.length} blocs)
                </h2>
              </div>
              
              {contentBlocks.length === 0 ? (
                <div className="bg-white border border-dashed rounded-lg p-8 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">Aucun bloc de contenu</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="blocks-list">
                  {contentBlocks.map((block, index) => (
                    <BlockEditor
                      key={index}
                      block={block}
                      index={index}
                      onUpdate={(newBlock) => updateBlock(index, newBlock)}
                      onDelete={() => deleteBlock(index)}
                      onMoveUp={() => moveBlock(index, -1)}
                      onMoveDown={() => moveBlock(index, 1)}
                      isFirst={index === 0}
                      isLast={index === contentBlocks.length - 1}
                      photos={photos}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Cover Photo */}
            <div className="bg-white border rounded-lg p-4">
              <label className="block text-sm font-medium text-primary mb-2">Photo de couverture</label>
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 bg-muted rounded overflow-hidden shrink-0">
                  {coverPhotoId && getPhotoThumb(coverPhotoId) ? (
                    <img src={getPhotoThumb(coverPhotoId)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
                <Input
                  value={coverPhotoId}
                  onChange={(e) => setCoverPhotoId(e.target.value)}
                  placeholder="ID de la photo"
                  className="font-mono text-sm"
                />
              </div>
            </div>

            {/* Category & Destination */}
            <div className="bg-white border rounded-lg p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Catégorie *</label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Destination</label>
                <Select value={destinationId || "none"} onValueChange={(v) => setDestinationId(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Optionnel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {destinations.map(dest => (
                      <SelectItem key={dest.id} value={dest.id}>{dest.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* SEO */}
            <div className="bg-white border rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-primary">SEO</h3>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Titre SEO ({seoTitle.length}/60)
                </label>
                <Input
                  value={seoTitle}
                  onChange={(e) => setSeoTitle(e.target.value)}
                  placeholder="Titre pour les moteurs de recherche"
                  maxLength={60}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Meta description ({metaDescription.length}/160)
                </label>
                <Textarea
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="Description pour les moteurs de recherche"
                  rows={2}
                  maxLength={160}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Tags</label>
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="voyage, croisière, méditerranée"
                />
                <p className="text-xs text-muted-foreground mt-1">Séparés par des virgules</p>
              </div>
            </div>

            {/* Éditorial & transparence (Phase 14 STEP 3.3/4.2/4.3) */}
            <div className="bg-white border rounded-lg p-4 space-y-4" data-testid="editorial-meta-card">
              <h3 className="font-medium text-primary">Éditorial & transparence</h3>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Type éditorial</label>
                <Select value={editorialType || "none"} onValueChange={(v) => setEditorialType(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="editorial-type-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    <SelectItem value="actualite">Actualité</SelectItem>
                    <SelectItem value="reportage">Reportage</SelectItem>
                    <SelectItem value="carnet">Carnet</SelectItem>
                    <SelectItem value="test_avis">Test &amp; Avis</SelectItem>
                    <SelectItem value="analyse">Analyse</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between border rounded-md px-3 py-2">
                <span className="text-sm text-primary">Afficher un encart transparence</span>
                <Switch checked={showDisclosure} onCheckedChange={setShowDisclosure} data-testid="show-disclosure-switch" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Type de transparence</label>
                <Input value={disclosureType} onChange={(e) => setDisclosureType(e.target.value)} placeholder="invitation, voyage_offert, produit_prete…" data-testid="disclosure-type-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Partenaire concerné</label>
                <Input value={disclosurePartner} onChange={(e) => setDisclosurePartner(e.target.value)} placeholder="Nom du partenaire" data-testid="disclosure-partner-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Texte de transparence</label>
                <Textarea value={disclosureText} onChange={(e) => setDisclosureText(e.target.value)} rows={3} placeholder="Ce reportage a été réalisé à l'invitation de… ITERCATUS conserve l'entière maîtrise de son contenu éditorial." data-testid="disclosure-text-input" />
              </div>
              <hr className="border-border/60" />
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Note de correction / mise à jour</label>
                <Textarea value={correctionNote} onChange={(e) => setCorrectionNote(e.target.value)} rows={2} placeholder="Correction apportée le…" data-testid="correction-note-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Date de correction</label>
                <Input type="date" value={correctionDate} onChange={(e) => setCorrectionDate(e.target.value)} data-testid="correction-date-input" />
              </div>
            </div>

            {/* Audit assistance IA — INTERNE (Phase 14 STEP 4.4) */}
            <div className="bg-white border rounded-lg p-4 space-y-3" data-testid="ai-audit-card">
              <h3 className="font-medium text-primary">Audit assistance IA (interne)</h3>
              <p className="text-xs text-muted-foreground">Métadonnées internes — jamais affichées publiquement, aucune étiquette « IA » automatique sur l'article.</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-primary">Assistance IA utilisée</span>
                <Switch checked={!!aiMeta?.ai_assistance_used} onCheckedChange={(v) => setAiMeta((m) => ({ ...(m || {}), ai_assistance_used: v }))} data-testid="ai-used-switch" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-primary">Revu par un humain</span>
                <Switch checked={!!aiMeta?.human_reviewed} onCheckedChange={(v) => setAiMeta((m) => ({ ...(m || {}), human_reviewed: v }))} data-testid="ai-reviewed-switch" />
              </div>
              {aiMeta?.ai_assistance_types?.length ? (
                <p className="text-xs text-muted-foreground">Types : {aiMeta.ai_assistance_types.join(", ")}</p>
              ) : null}
              {aiMeta?.human_reviewer && <p className="text-xs text-muted-foreground">Validé par : {aiMeta.human_reviewer}</p>}
              {aiMeta?.human_validated_at && <p className="text-xs text-muted-foreground">Le : {String(aiMeta.human_validated_at).slice(0, 10)}</p>}
              <Button size="sm" variant="outline" onClick={handleSaveAi} disabled={savingAi} data-testid="ai-save-btn">
                {savingAi ? "…" : "Enregistrer l'audit IA"}
              </Button>
            </div>

            {/* Phase G2 suivi — Traductions FR → EN/ES/IT/ZH-CN/JA */}
            <TranslationsPanel
              resource="articles"
              id={id}
              disabled={!(status === "validated" || status === "published")}
            />

            {/* Status */}
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-medium text-primary mb-2">Statut</h3>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="to_review">À relire</SelectItem>
                  <SelectItem value="validated">Validé</SelectItem>
                  <SelectItem value="published">Publié</SelectItem>
                  <SelectItem value="archived">Archivé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
